import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { 
  Plus, Search, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight,
  Filter, FileText, User, RefreshCw, X, Box, ArrowUpDown
} from 'lucide-react';
import toast from 'react-hot-toast';
import { exportToExcel } from './exportUtils'; 
import { usePermissions } from './usePermissions'; 

const SRList = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language.startsWith('ar') || i18n.language.startsWith('ur');
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { canCreate } = usePermissions('sr'); // الصلاحيات

  // --- Session Data ---
  const session = JSON.parse(localStorage.getItem('user') || '{}');
  const sessionSite = session.site_id || '';
  const sessionOrg = session.org_id || '';

  // --- State ---
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [showAllSites, setShowAllSites] = useState(searchParams.get('all_sites') === 'true');

  // --- URL Params ---
  const page = parseInt(searchParams.get('page')) || 1;
  const limit = 50;
  const q = searchParams.get('q') || "";
  const srnum = searchParams.get('srnum') || "";
  const description = searchParams.get('description') || "";
  const status = searchParams.get('status') || "";
  const reportedby = searchParams.get('reportedby') || "";
  const priority = searchParams.get('priority') || "";
  const sortBy = searchParams.get('sortBy') || "reportdate";
  const sortDir = searchParams.get('sortDir') || "desc";

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const effectiveSite = !showAllSites && !searchParams.get('site_id') ? sessionSite : searchParams.get('site_id');

      const res = await axios.get('/api/sr/list', {
        params: { 
          page, limit, q, srnum, description, status, reportedby, priority,
          site_id: effectiveSite, org_id: sessionOrg, all_sites: showAllSites,
          sortBy, sortDir 
        },
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setData(res.data.data);
      setTotalCount(res.data.total);
    } catch (err) {
      toast.error(t('common.load_error', "Failed to load Data"));
    } finally {
      setLoading(false);
    }
  }, [page, q, srnum, description, status, reportedby, priority, searchParams.get('site_id'), sortBy, sortDir, showAllSites, sessionSite, sessionOrg, t]);

  useEffect(() => {
    const timer = setTimeout(() => fetchData(), 100);
    return () => clearTimeout(timer);
  }, [fetchData]);

  const updateFilters = (newFilters) => {
    const params = Object.fromEntries(searchParams.entries());
    const updated = { ...params, ...newFilters };
    Object.keys(updated).forEach(key => !updated[key] && delete updated[key]);
    setSearchParams(updated);
  };

  const handleSort = (column) => {
    const direction = sortBy === column && sortDir === 'asc' ? 'desc' : 'asc';
    updateFilters({ sortBy: column, sortDir: direction, page: 1 });
  };

  const handleRowClick = (id) => {
    const activeFilters = Object.fromEntries(searchParams.entries());
    const effectiveSite = !showAllSites && !activeFilters.site_id ? sessionSite : activeFilters.site_id;
    let navParams = { ...activeFilters, org_id: sessionOrg };
    if (effectiveSite) navParams.site_id = effectiveSite; else delete navParams.site_id;
    if (showAllSites) navParams.all_sites = true;
    
    const queryParams = new URLSearchParams(navParams).toString();
    navigate(`/sr/${id}${queryParams ? '?' + queryParams : ''}`);
  };

  const handleClearFilters = () => {
    setSearchParams({});
    setShowAllSites(false);
  };

  useEffect(() => {
    const openAllSites = () => { setShowAllSites(true); updateFilters({ page: 1, all_sites: true }); };
    const viewMySite = () => { setShowAllSites(false); updateFilters({ page: 1, all_sites: false }); };
    
    const exportData = () => {
        const activeFilters = Object.fromEntries(searchParams.entries());
        const effectiveSite = !showAllSites && !activeFilters.site_id ? sessionSite : activeFilters.site_id;
        let exportParams = { ...activeFilters, org_id: sessionOrg };
        if (effectiveSite) exportParams.site_id = effectiveSite; else delete exportParams.site_id;

        const columnsMap = {
            "srnum": t('sr.fields.srnum', 'SR Number'),
            "description": t('sr.fields.description', 'Description'),
            "status": t('common.status', 'Status'),
            "reportedby": t('sr.fields.reportedby', 'Reported By'),
            "priority": t('sr.fields.priority', 'Priority'),
            "reportdate": t('sr.fields.reportdate', 'Report Date')
        };
        exportToExcel('servicerequest', exportParams, columnsMap, t); // غير الكيان حسب الباك إند
    };

    window.addEventListener('GLOBAL_VIEW_ALL_SITES', openAllSites);
    window.addEventListener('GLOBAL_VIEW_MY_SITE', viewMySite);
    window.addEventListener('GLOBAL_EXPORT', exportData);
    
    return () => {
        window.removeEventListener('GLOBAL_VIEW_ALL_SITES', openAllSites);
        window.removeEventListener('GLOBAL_VIEW_MY_SITE', viewMySite);
        window.removeEventListener('GLOBAL_EXPORT', exportData);
    };
  }, [searchParams, showAllSites, sessionSite, sessionOrg, t]);

  const totalPages = Math.ceil(totalCount / limit) || 1;

  return (
    <div className={`flex flex-col h-full bg-surface-panel text-[11px] font-black uppercase tracking-tighter ${isRTL ? 'font-cairo text-right' : 'font-sans text-left'}`} dir={isRTL ? 'rtl' : 'ltr'}>

      {/* Header Section */}
      <div className="bg-primary-dark p-3 flex justify-between items-center text-white z-20 shadow-sm">
        <div className={`flex items-center gap-3 ${isRTL ? 'mr-4' : 'ml-4'}`}>
          <div className="bg-primary/20 p-2 rounded"><FileText size={18} className="text-sky" /></div>
          <div>
            <h1 className="text-[14px] font-black flex items-center gap-2">
              {t('sr.list.title', 'Service Request Tracking')}
              <span className={`text-[9px] px-2 py-0.5 rounded-full border ${showAllSites ? 'bg-status-danger/20 text-status-danger border-status-danger/50' : 'bg-status-info/20 text-status-info border-status-info/50'}`}>
                {showAllSites ? t('common.global_view', 'GLOBAL VIEW') : `${t('common.site', 'SITE')}: ${sessionSite}`}
              </span>
            </h1>
          </div>
        </div>
        <div className="flex gap-3 items-center">
            <button onClick={handleClearFilters} className="flex items-center gap-2 px-4 py-2 text-ink-secondary hover:text-status-danger font-bold transition-colors">
              <X size={16}/> {t('common.clear_filters', 'CLEAR FILTERS')}
            </button>
            <button onClick={() => fetchData()} className="p-2 text-ink-secondary hover:text-white transition-colors">
              <RefreshCw size={16} className={loading ? "animate-spin text-status-warning" : ""} />
            </button>
            {canCreate && (
              <button onClick={() => navigate('/sr/new')} className="bg-primary hover:bg-primary-hover px-6 py-2 rounded-sm text-[11px] font-black flex items-center gap-2 transition-all shadow-lg active:scale-95">
                <Plus size={16} /> {t('sr.list.new', 'NEW REQUEST')}
              </button>
            )}
        </div>
      </div>

      {/* Main Table */}
      <div className="flex-1 overflow-auto p-4 custom-scrollbar">
        <div className="bg-surface-card rounded-sm border shadow-sm overflow-hidden">
          <table className="w-full text-left border-collapse">
            <thead className="bg-surface-panel text-ink-secondary text-[10px] tracking-widest sticky top-0 z-10">
              <tr>
                <SortableHeader label={t('sr.fields.srnum', 'SR Number')} sortKey="srnum" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} isRTL={isRTL} />
                <SortableHeader label={t('sr.fields.description', 'Description')} sortKey="description" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} isRTL={isRTL} />
                <SortableHeader label={t('common.status', 'Status')} sortKey="status" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} align="center" isRTL={isRTL} />
                <SortableHeader label={t('sr.fields.reportedby', 'Reported By')} sortKey="reportedby" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} isRTL={isRTL} />
                <SortableHeader label={t('sr.fields.reportdate', 'Report Date')} sortKey="reportdate" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} align="center" isRTL={isRTL} />
                <SortableHeader label={t('sr.fields.priority', 'Priority')} sortKey="priority" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} align="center" isRTL={isRTL} />
              </tr>
              {/* Filter Row */}
              <tr className="bg-surface-card border-b border-line">
                <td className="p-1 border-x border-line-subtle">
                  <div className={`relative flex items-center ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <Search className={`absolute text-ink-secondary ${isRTL ? 'right-2' : 'left-2'}`} size={12} />
                    <FilterInput value={q} onChange={v => updateFilters({ q: v, page: 1 })} placeholder={t('common.search')} className={isRTL ? 'pr-6' : 'pl-6'}/>
                  </div>
                </td>
                <td className="p-1 border-x border-line-subtle"><FilterInput value={description} onChange={v => updateFilters({ description: v, page: 1 })} /></td>
                <td className="p-1 border-x border-line-subtle">
                  <select className="w-full p-1.5 text-primary font-bold outline-none bg-transparent text-center" value={status} onChange={e => updateFilters({ status: e.target.value, page: 1 })}>
                    <option value="">{t('common.all', 'ALL')}</option>
                    <option value="NEW">NEW</option>
                    <option value="QUEUED">QUEUED</option>
                    <option value="INPRG">INPRG</option>
                    <option value="RESOLVED">RESOLVED</option>
                  </select>
                </td>
                <td className="p-1 border-x border-line-subtle"><FilterInput value={reportedby} onChange={v => updateFilters({ reportedby: v, page: 1 })} /></td>
                <td className="p-1 border-x border-line-subtle bg-surface-panel/30"></td>
                <td className="p-1 border-x border-line-subtle"><FilterInput value={priority} onChange={v => updateFilters({ priority: v, page: 1 })} center /></td>
              </tr>
            </thead>

            <tbody className="divide-y divide-line-subtle text-[12px] font-bold">
              {loading ? (
                <tr><td colSpan="6" className="p-20 text-center text-ink-secondary animate-pulse tracking-[0.2em]">{t('common.syncing', 'Syncing Data...')}</td></tr>
              ) : data.length === 0 ? (
                <tr><td colSpan="6" className="p-20 text-center text-ink-secondary italic">{t('sr.messages.no_records', 'No service requests found')}</td></tr>
              ) : data.map((sr) => (
                <tr key={sr.srnum} onClick={() => handleRowClick(sr.srnum)} className={`hover:bg-primary-soft cursor-pointer group ${isRTL ? 'text-right' : 'text-left'}`}>
                  <td className="p-4 font-black text-primary group-hover:underline border-x border-line-subtle">{sr.srnum}</td>
                  <td className="p-4 text-ink border-x border-line-subtle">{sr.description}</td>
                  <td className="p-4 border-x border-line-subtle text-center">
                    <span className={`px-3 py-1 rounded text-[9px] font-black ${ sr.status === 'NEW' ? 'bg-status-warning-bg text-status-warning' : sr.status === 'QUEUED' ? 'bg-status-info-bg text-status-info' : 'bg-status-success-bg text-status-success' }`}>
                      {sr.status}
                    </span>
                  </td>
                  <td className={`p-4 border-x border-line-subtle text-ink`}><div className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}><User size={14} className="text-ink-secondary"/> {sr.reportedby}</div></td>
                  <td className="p-4 border-x border-line-subtle text-ink-secondary text-center font-mono">{new Date(sr.reportdate).toLocaleDateString()}</td>
                  <td className="p-4 border-x border-line-subtle text-center">
                    <span className={`text-[11px] font-black ${sr.priority === 1 ? 'text-status-danger' : 'text-ink-secondary'}`}>{sr.priority || '-'}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination Footer */}
      <div className={`bg-surface-card border-t p-2 px-8 flex justify-between items-center shadow-inner z-30 font-black text-[10px] text-ink-secondary ${isRTL ? 'flex-row-reverse' : ''}`}>
        <div className={`flex items-center gap-6 ${isRTL ? 'flex-row-reverse' : ''}`}>
            <div className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                <Box size={14} className="text-primary"/>
                <span>{t('common.showing', 'Showing')}: <span className="text-primary">{(page-1)*limit+1}-{Math.min(page*limit, totalCount)}</span></span>
            </div>
            <div className={`flex items-center gap-2 border-line ${isRTL ? 'border-r pr-6 flex-row-reverse' : 'border-l pl-6'}`}>
                <Filter size={14} className="text-status-warning"/>
                <span>{t('common.total', 'Total')}: <span className="text-status-warning">{totalCount}</span></span>
            </div>
        </div>

        <div className={`flex items-center gap-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
            <div className={`flex items-center gap-1 bg-surface-panel rounded px-2 py-1 border border-line ${isRTL ? 'flex-row-reverse' : ''}`}>
                <button disabled={page === 1} onClick={() => updateFilters({page: 1})} className="p-1.5 hover:text-primary disabled:opacity-30 transition-all"><ChevronsLeft size={16} className={isRTL ? 'rotate-180' : ''}/></button>
                <button disabled={page === 1} onClick={() => updateFilters({page: page - 1})} className="p-1.5 hover:text-primary disabled:opacity-30 transition-all"><ChevronLeft size={16} className={isRTL ? 'rotate-180' : ''}/></button>
                <div className="px-4 text-[11px] font-mono border-x border-line mx-1">
                    <span className="text-primary text-sm">{page}</span> / {totalPages}
                </div>
                <button disabled={page >= totalPages} onClick={() => updateFilters({page: page + 1})} className="p-1.5 hover:text-primary disabled:opacity-30 transition-all"><ChevronRight size={16} className={isRTL ? 'rotate-180' : ''}/></button>
                <button disabled={page >= totalPages} onClick={() => updateFilters({page: totalPages})} className="p-1.5 hover:text-primary disabled:opacity-30 transition-all"><ChevronsRight size={16} className={isRTL ? 'rotate-180' : ''}/></button>
            </div>
        </div>
      </div>
    </div>
  );
};

// --- Helper Components ---
const SortableHeader = ({ label, sortKey, sortBy, sortDir, onSort, align = "left", isRTL }) => (
  <th className={`p-4 border-x border-line cursor-pointer hover:bg-primary-soft transition-colors text-${align === 'center' ? 'center' : (isRTL ? 'right' : 'left')}`} onClick={() => onSort(sortKey)}>
      <div className={`flex items-center gap-2 ${align === 'center' ? 'justify-center' : ''} ${isRTL ? 'flex-row-reverse' : ''}`}>
          {label}
          <ArrowUpDown size={12} className={sortBy === sortKey ? 'text-primary' : 'text-ink-secondary'} />
      </div>
  </th>
);

const FilterInput = ({ value, onChange, placeholder, center = false, className = "" }) => (
  <input
    className={`w-full p-1.5 text-primary font-bold outline-none bg-transparent placeholder-ink-secondary ${center ? 'text-center' : ''} ${className}`}
    placeholder={placeholder} value={value} onChange={e => onChange(e.target.value)}
  />
);

export default SRList;